import { type ColDef, type NewColumnsLoadedEvent } from "ag-grid-community"
import { dequal } from "dequal"
import { useCallback, useEffect, useMemo, useRef } from "react"

import { placeLoadedColumns } from "~/components/planning/ProductTable/utils/columnPlacement"
import { liveColumns } from "~/components/planning/ProductTable/utils/columnVisibility"
import { type SavedStateMigration, subscribeViewChange } from "~/hooks/useGridState"
import { useRegisteredGridEffect } from "~/hooks/useRegisteredGridEffect"
import { useStableValue } from "~/hooks/useStableValue"
import {
  type ColumnGroups,
  chooserIdsOf,
  expandGroups,
  type ForcedVisibility,
  gridOwnedColDefs,
  groupedVisibility,
  keepAbsentColumns,
  migrateGridOwnedState,
  NO_GROUPS,
  registerGridOwnedRules,
  visibilityPatchBy,
} from "~/utils/gridOwnedColumns"

const FREE: ForcedVisibility = () => undefined

/**
 * Column visibility owned by the grid of a `DataTableBaseV2` table: the table passes its colDefs and
 * gets back the colDefs to render, the `chooserProps` to register as `gridOwnedChooserProps`, and the
 * props that hand the grid its saved-state migration and late-column placement. Columns start as
 * `forcedVisibility` says, else shown when in `defaultColumnIds`; `columnGroups` members follow their
 * group's chooser entry.
 * `forcedVisibility` and `columnGroups` are re-applied whenever their identity changes, so memoize
 * them on what they read. The chooser and a reset commit through `commitShownColumns`, which applies
 * them too.
 */
export function useGridOwnedColumns<T>(
  tableId: string,
  colDefs: readonly ColDef<T>[],
  {
    defaultColumnIds,
    forcedVisibility = FREE,
    columnGroups = NO_GROUPS,
  }: {
    defaultColumnIds: readonly string[]
    forcedVisibility?: ForcedVisibility
    columnGroups?: ColumnGroups
  },
) {
  const defaultSet = useMemo(
    () => new Set(expandGroups(defaultColumnIds, columnGroups)),
    [defaultColumnIds, columnGroups],
  )
  const columnDefs = useMemo(
    () => gridOwnedColDefs(colDefs, defaultSet, forcedVisibility, columnGroups),
    [colDefs, defaultSet, forcedVisibility, columnGroups],
  )

  // Stable by content: colDefs rebuild far more often than the set of column ids changes.
  const chooserIds = useMemo(
    () => chooserIdsOf(columnDefs, forcedVisibility, columnGroups),
    [columnDefs, forcedVisibility, columnGroups],
  )
  const chooserColumnIds = useStableValue(chooserIds, dequal)
  const chooserProps = useMemo(
    () => ({ columnIds: chooserColumnIds, defaultColumnIds, columnGroups }),
    [chooserColumnIds, defaultColumnIds, columnGroups],
  )

  // A ref, not useEventCallback: the grid keeps the migration it registered with and runs it during render.
  const rulesRef = useRef({ forcedVisibility, columnGroups })
  // eslint-disable-next-line react-hooks/refs
  rulesRef.current = { forcedVisibility, columnGroups }

  useEffect(
    () =>
      registerGridOwnedRules(tableId, {
        get forced() {
          return rulesRef.current.forcedVisibility
        },
        get groups() {
          return rulesRef.current.columnGroups
        },
      }),
    [tableId],
  )

  // Re-applied after a view apply too: its state lands after a rule change the view also made.
  useRegisteredGridEffect(
    tableId,
    (api) => {
      const enforce = () => {
        if (api.isDestroyed()) return
        const columns = liveColumns(api)
        let visible: Set<string> | undefined
        const isShown = (colId: string) =>
          (visible ??= new Set(columns.filter((c) => c.visible).map((c) => c.colId))).has(colId)
        const { forcedVisibility: forced, columnGroups: groups } = rulesRef.current
        const rule = groupedVisibility(forced, groups, isShown)
        const state = visibilityPatchBy(columns, rule)
        if (state.length > 0) api.applyColumnState({ state })
      }
      enforce()
      return subscribeViewChange(tableId, enforce)
    },
    [forcedVisibility, columnGroups],
  )
  const migrateSavedState = useCallback<SavedStateMigration>(
    (state, columns, selectedColIds) =>
      migrateGridOwnedState(
        state,
        columns,
        selectedColIds,
        rulesRef.current.forcedVisibility,
        rulesRef.current.columnGroups,
      ),
    [],
  )

  // Columns that had no data when the grid applied its state take that state once they have.
  const onNewColumnsLoaded = useCallback(
    ({ api }: NewColumnsLoadedEvent<T>) => {
      if (!api.isDestroyed())
        placeLoadedColumns(api, tableId, { isLate: () => true, restoreWidths: true })
    },
    [tableId],
  )

  return {
    columnDefs,
    chooserProps,
    gridProps: { migrateSavedState, mergeSavedState: keepAbsentColumns, onNewColumnsLoaded },
  }
}
